import React from "react";
import { Card } from "@/components/ui/card";
import { fmtPct, verdictLabel } from "@/lib/format";
import { ArrowUpRight, ArrowDownRight } from "lucide-react";

export const PageHeader = ({ title, subtitle, action, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7" data-testid={testid}>
    <div>
      <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">{title}</h1>
      {subtitle && <p className="text-sm text-muted-foreground mt-1.5 max-w-2xl">{subtitle}</p>}
    </div>
    {action}
  </div>
);

// Rotating chip + underline tints so a KPI row reads as a set, not four clones.
const TINTS = [
  { chip: "bg-[hsl(var(--chart-1)/0.14)] text-[hsl(var(--chart-1))]", bar: "bg-[hsl(var(--chart-1))]" },
  { chip: "bg-[hsl(var(--chart-2)/0.18)] text-[hsl(var(--chart-2))]", bar: "bg-[hsl(var(--chart-2))]" },
  { chip: "bg-[hsl(var(--chart-4)/0.16)] text-[hsl(var(--chart-4))]", bar: "bg-[hsl(var(--chart-4))]" },
  { chip: "bg-[hsl(var(--chart-3)/0.14)] text-[hsl(var(--chart-3))]", bar: "bg-[hsl(var(--chart-3))]" },
];

export const Kpi = ({ label, value, change, prefix, testid, accent, icon: Icon, tint = 0 }) => {
  const s = TINTS[tint % TINTS.length];
  return (
    <Card className={`p-5 rounded-2xl border-border/60 shadow-premium hover:-translate-y-px transition-transform ${accent ? "card-premium" : ""}`}
      data-testid={testid}>
      <div className="flex items-center gap-2.5">
        {Icon && <span className={`icon-chip ${s.chip}`}><Icon className="h-4 w-4" /></span>}
        <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</div>
      </div>
      <div className="mt-3 text-2xl sm:text-[28px] leading-none font-semibold numeric text-foreground">{prefix}{value}</div>
      <div className={`kpi-underline mt-3 ${s.bar}`} />
      {change !== undefined && change !== null && (
        <div className={`mt-2.5 text-sm font-semibold flex items-center gap-1 ${change >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"}`}>
          {change >= 0 ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
          <span className="numeric">{fmtPct(change)}</span>
        </div>
      )}
    </Card>
  );
};

export const LiveBadge = () => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-emerald-700 dark:text-emerald-400">
    <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 live-dot" /> Live
  </span>
);

export const Delta = ({ value, className = "" }) => (
  <span className={`numeric font-semibold ${Number(value) >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400"} ${className}`}>
    {fmtPct(value)}
  </span>
);

/** Empty states get the owl: a small mark on a soft indigo halo with the lime swoosh under it. */
export const OwlSpot = ({ size = 56, className = "" }) => (
  <span className={`relative inline-flex items-center justify-center shrink-0 ${className}`}
    style={{ width: size, height: size }} aria-hidden="true" data-testid="owl-spot">
    <span className="absolute inset-0 rounded-2xl"
      style={{ background: "radial-gradient(circle at 50% 40%, hsl(var(--accent)) 0%, transparent 72%)" }} />
    <img src="/tharwy-mark.png" alt="" className="relative dark:hidden"
      style={{ width: size * 0.66, height: size * 0.66, objectFit: "contain" }} />
    <img src="/tharwy-mark-light.png" alt="" className="relative hidden dark:block"
      style={{ width: size * 0.66, height: size * 0.66, objectFit: "contain" }} />
    <span className="absolute rounded-full"
      style={{ bottom: size * 0.06, width: size * 0.5, height: 3, background: "var(--tharwy-lime)", opacity: 0.9 }} />
  </span>
);

export const EmptyState = ({ title, hint, action, testid, compact }) => (
  compact ? (
    <div className="flex items-center gap-3 py-5 px-1" data-testid={testid}>
      <OwlSpot size={34} />
      <div>
        <div className="text-sm font-semibold text-foreground">{title}</div>
        {hint && <div className="text-xs text-muted-foreground mt-0.5 max-w-xs">{hint}</div>}
      </div>
      {action && <div className="ms-auto">{action}</div>}
    </div>
  ) : (
    <div className="flex flex-col items-start text-start py-12 px-2" data-testid={testid}>
      <OwlSpot className="mb-4" />
      <div className="font-heading text-lg font-semibold text-foreground">{title}</div>
      {hint && <div className="text-sm text-muted-foreground mt-1.5 max-w-sm">{hint}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
);

const PILL = "inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-[0.1em]";

export const HalalBadge = () => (
  <span className={`${PILL} bg-emerald-500/12 text-emerald-700 dark:text-emerald-400`}>Halal</span>
);

const COMPLIANCE_CLASS = {
  "Compliant": "bg-emerald-500/12 text-emerald-700 dark:text-emerald-400",
  "Questionable": "bg-amber-500/16 text-amber-700 dark:text-amber-400",
  "Non-Compliant": "bg-red-500/12 text-red-700 dark:text-red-400",
};

export const ComplianceBadge = ({ status, testid }) => (
  <span data-testid={testid} className={`${PILL} ${COMPLIANCE_CLASS[status] || COMPLIANCE_CLASS["Questionable"]}`}>
    {verdictLabel(status)}
  </span>
);

export const Stat = ({ value, label }) => (
  <div>
    <div className="text-xl sm:text-2xl font-semibold numeric text-foreground">{value}</div>
    <div className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground mt-1">{label}</div>
  </div>
);
